import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { BuilderFlow } from '@/components/builder/BuilderFlow';
import { BuilderHeader } from '@/components/builder/BuilderHeader';
import { BuilderSettings } from '@/components/builder/BuilderSettings';
import { BuilderStyle } from '@/components/builder/BuilderStyle';
import { BuilderTriggers } from '@/components/builder/BuilderTriggers';
import { BuilderIntegrations } from '@/components/builder/BuilderIntegrations';
import { BuilderDashboard } from '@/components/builder/BuilderDashboard';
import { BuilderKnowledgeBase } from '@/components/builder/BuilderKnowledgeBase';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import PortalLayout from '@/components/portal/PortalLayout';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';
import { useBuilder } from '@/contexts/BuilderContext';
import { TriggerRule } from '@/types/theme';
import { useQuestionMutations } from '@/hooks/mutations/useQuestionMutations';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const TAB_DASHBOARD = "dashboard";
const TAB_FLOW = "flow";
const TAB_SETTINGS = "settings";
const TAB_STYLE = "style";
const TAB_TRIGGERS = "triggers";
const TAB_KNOWLEDGE = "knowledge";
const TAB_INTEGRATIONS = "integrations";

export const PortalBuilderContent = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  
  // Get builder context
  const {
    state,
    updateSettings,
    updateTheme,
    updateQuestions,
    updateTriggerRules,
    updateBasicInfo,
    setDirty,
    resetDirty,
    setActiveTab,
    getChatbot,
    getSettings,
    getTheme,
    getQuestions,
    getTriggerRules,
    debouncedSave,
    saveTheme,
    saveTriggerRules,
    refetch
  } = useBuilder();

  // Initialize question mutations with the chatbot ID from state
  const questionMutations = useQuestionMutations(state.chatbot?.id);
  
  // Get all data from context (single source of truth)
  console.log('PortalBuilderContent: Getting data from context');

  // Manual save function to replace auto-save
  const handleManualSave = useCallback(() => {
    console.log('Header save button clicked!', { isDirty: state.isDirty, chatbot: !!state.chatbot });

    if (state.chatbot) {
      const chatbot = state.chatbot;
      const settings = state.chatbot.settings;
      const triggerRules = getTriggerRules();

      console.log('Saving with settings:', settings);
      console.log('Saving trigger rules:', triggerRules);

      // Save main chatbot data
      debouncedSave({
        settings: settings,
        name: chatbot.name,
        description: chatbot.description,
        title: chatbot.title,
        welcome_message: chatbot.welcome_message,
        completion_message: chatbot.completion_message
      });

      // Save trigger rules if there are any
      if (triggerRules && triggerRules.length > 0) {
        console.log('🎯 Saving trigger rules via manual save...');
        saveTriggerRules(triggerRules);
      }

      toast({
        title: "Saving changes...",
        description: "Your chatbot settings and trigger rules are being saved.",
      });
    } else {
      console.log('No chatbot data available for save');
    }
  }, [state.chatbot, debouncedSave, saveTriggerRules, getTriggerRules, toast]);

  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [urlTabInitialized, setUrlTabInitialized] = useState(false);

  // Initialize active tab from URL parameter on mount
  useEffect(() => {
    if (!urlTabInitialized) {
      const tabFromUrl = searchParams.get('tab');
      const validTabs = [TAB_DASHBOARD, TAB_FLOW, TAB_STYLE, TAB_TRIGGERS, TAB_KNOWLEDGE, TAB_INTEGRATIONS, TAB_SETTINGS];

      console.log('Initial URL tab setup:', { tabFromUrl, currentActiveTab: state.activeTab });

      if (tabFromUrl && validTabs.includes(tabFromUrl)) {
        console.log('Setting initial tab from URL:', tabFromUrl);
        setActiveTab(tabFromUrl);
      } else if (!tabFromUrl) {
        console.log('No tab in URL, setting default dashboard and updating URL');
        setActiveTab(TAB_DASHBOARD);
        setSearchParams(prev => {
          prev.set('tab', TAB_DASHBOARD);
          return prev;
        });
      }
      setUrlTabInitialized(true);
    }
  }, [searchParams, setActiveTab, setSearchParams, state.activeTab, urlTabInitialized]);

  // Keep URL in sync with tab changes
  useEffect(() => {
    if (urlTabInitialized) {
      const tabFromUrl = searchParams.get('tab');
      if (tabFromUrl !== state.activeTab) {
        console.log('Syncing URL with active tab:', state.activeTab);
        setSearchParams(prev => {
          prev.set('tab', state.activeTab);
          return prev;
        });
      }
    }
  }, [state.activeTab, searchParams, setSearchParams, urlTabInitialized]);

  // Handle tab changes with unsaved changes warning
  const handleTabChange = (newTab: string) => {
    if (state.unsavedChanges) {
      setPendingTab(newTab);
      setIsAlertOpen(true);
    } else {
      setActiveTab(newTab);
      // Update URL parameter
      setSearchParams(prev => {
        prev.set('tab', newTab);
        return prev;
      });
    }
  };

  const discardChangesAndSwitchTab = () => {
    if (pendingTab) {
      setActiveTab(pendingTab);
      resetDirty();
      // Update URL parameter
      setSearchParams(prev => {
        prev.set('tab', pendingTab);
        return prev;
      });
      setPendingTab(null);
    }
    setIsAlertOpen(false);
  };

  const cancelTabSwitch = () => {
    setPendingTab(null);
    setIsAlertOpen(false);
  };

  // DISABLED AUTO-SAVE to prevent race conditions with manual saves
  // Auto-save when settings change (with additional debounce)
  // useEffect(() => {
  //   if (state.isDirty && state.chatbot) {
  //     const chatbot = state.chatbot;
  //     const settings = state.chatbot.settings;
  //
  //     // Add a small delay to prevent rapid-fire saves
  //     const timeoutId = setTimeout(() => {
  //       debouncedSave({
  //         settings: settings,
  //         name: chatbot.name,
  //         description: chatbot.description,
  //         title: chatbot.title,
  //         welcome_message: chatbot.welcome_message,
  //         completion_message: chatbot.completion_message
  //       });
  //     }, 500); // 500ms delay before triggering save
  //
  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [state.isDirty, state.chatbot?.id, debouncedSave]); // Only depend on stable values

  // Show loading state
  if (state.isLoading) {
    return (
      <PortalLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center min-h-64">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-muted-foreground">Loading chatbot...</p>
            </div>
          </div>
        </div>
      </PortalLayout>
    );
  }

  // Show error state
  if (state.error) {
    return (
      <PortalLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center min-h-64">
            <div className="text-center space-y-4">
              <p className="text-destructive">{state.error}</p>
              <button
                onClick={() => refetch()}
                className="text-primary hover:underline mr-4"
              >
                Retry
              </button>
              <button
                onClick={() => navigate('/portal/chatbots')}
                className="text-primary hover:underline"
              >
                Back to Chatbots
              </button>
            </div>
          </div>
        </div>
      </PortalLayout>
    );
  }

  // Show chatbot initializer if no chatbot
  const chatbot = getChatbot();

  // Show loading state while chatbot is being loaded
  if (state.isLoading || !chatbot) {
    return (
      <PortalLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading chatbot...</p>
            </div>
          </div>
        </div>
      </PortalLayout>
    );
  }

  // Show error state if there's an error loading the chatbot
  if (state.error) {
    return (
      <PortalLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <p className="text-destructive mb-4">Error loading chatbot: {state.error}</p>
              <Button onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          </div>
        </div>
      </PortalLayout>
    );
  }

  // Main builder interface
  return (
    <PortalLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <BuilderHeader
              chatbot={chatbot}
              onCopyEmbedCode={() => {
                const embedCode = `<script src="https://app.chatoffers.ai/embed.js" data-chatbot-id="${chatbot.id}"></script>`;
                navigator.clipboard.writeText(embedCode);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
                toast({
                  title: "Embed code copied!",
                  description: "The embed code has been copied to your clipboard.",
                });
              }}
              copied={copied}
            />
          </div>

          {/* Save Button */}
          {state.isDirty && (
            <Button
              onClick={handleManualSave}
              disabled={state.isSaving}
              className="ml-4"
            >
              {state.isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          )}
        </div>

        <Separator />
        
        <Tabs value={state.activeTab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value={TAB_DASHBOARD}>Dashboard</TabsTrigger>
            <TabsTrigger value={TAB_FLOW}>Flow</TabsTrigger>
            <TabsTrigger value={TAB_STYLE}>Style</TabsTrigger>
            <TabsTrigger value={TAB_TRIGGERS}>Triggers</TabsTrigger>
            <TabsTrigger value={TAB_KNOWLEDGE}>Knowledge</TabsTrigger>
            <TabsTrigger value={TAB_INTEGRATIONS}>Integrations</TabsTrigger>
            <TabsTrigger value={TAB_SETTINGS}>Settings</TabsTrigger>
          </TabsList>
          
          <TabsContent value={TAB_DASHBOARD} className="space-y-4">
            <BuilderDashboard 
              chatbot={chatbot}
              reportUnsavedChanges={(isDirty) => setDirty(TAB_DASHBOARD, isDirty)}
            />
          </TabsContent>
          
          <TabsContent value={TAB_FLOW} className="space-y-4">
            <BuilderFlow
              chatFlow={{
                welcomeMessage: chatbot.welcome_message,
                completionMessage: chatbot.completion_message,
                questions: getQuestions()
              }}
              onUpdateChatFlow={(updates) => {
                updateBasicInfo({
                  welcome_message: updates.welcomeMessage,
                  completion_message: updates.completionMessage
                });
              }}
              questionTypes={[
                { value: "text", label: "Text" },
                { value: "number", label: "Number" },
                { value: "button", label: "Button" },
                { value: "boolean", label: "Yes/No" },
                { value: "no_response", label: "No Response" },
              ]}
              onAddQuestion={async () => {
                const { addQuestion } = questionMutations;
                try {
                  const newQuestionData = {
                    text: '',
                    type: 'text' as const,
                    options: [],
                    order: getQuestions().length,
                    shortname: '',
                    errormessage: '',
                    responsevalidation: '',
                    validation_rules: {},
                    validationType: 'none',
                    placeholder: '',
                    rich_content: null,
                    enable_ai_response: false,
                    ai_response_prompt: null
                  };
                  
                  // Add to database first
                  const result = await addQuestion.mutateAsync(newQuestionData);
                  
                  // Then update local state with the actual database record
                  updateQuestions([...getQuestions(), result]);
                } catch (error) {
                  console.error('Failed to add question:', error);
                  toast({
                    title: 'Error adding question',
                    description: 'Failed to add the question. Please try again.',
                    variant: 'destructive'
                  });
                }
              }}
              onUpdateQuestion={async (questionId, updates) => {
                // Skip database update for temporary questions
                if (questionId.startsWith('temp_')) {
                  const questions = getQuestions().map(q => 
                    q.id === questionId ? { ...q, ...updates } : q
                  );
                  updateQuestions(questions);
                  return;
                }
                
                const { updateQuestion } = questionMutations;
                try {
                  // Update in database
                  await updateQuestion.mutateAsync({ questionId, updates });
                  
                  // Update local state
                  const questions = getQuestions().map(q => 
                    q.id === questionId ? { ...q, ...updates } : q
                  );
                  updateQuestions(questions);
                } catch (error) {
                  console.error('Failed to update question:', error);
                  toast({
                    title: 'Error updating question',
                    description: 'Failed to update the question. Please try again.',
                    variant: 'destructive'
                  });
                }
              }}
              onDeleteQuestion={async (questionId) => {
                // Use the delete mutation from useQuestionMutations
                const { deleteQuestion } = questionMutations;
                try {
                  await deleteQuestion.mutateAsync(questionId);
                  // After successful deletion, update local state
                  const questions = getQuestions().filter(q => q.id !== questionId);
                  updateQuestions(questions);
                } catch (error) {
                  console.error('Failed to delete question:', error);
                  toast({
                    title: 'Error deleting question',
                    description: 'Failed to delete the question. Please try again.',
                    variant: 'destructive'
                  });
                }
              }}
              onReorderQuestions={async (reorderedQuestions) => {
                const { updateQuestionsOrder } = questionMutations;
                try {
                  // Update order in database
                  await updateQuestionsOrder.mutateAsync(reorderedQuestions);
                  
                  // Update local state
                  updateQuestions(reorderedQuestions);
                } catch (error) {
                  console.error('Failed to reorder questions:', error);
                  toast({
                    title: 'Error reordering questions',
                    description: 'Failed to save the new order. Please try again.',
                    variant: 'destructive'
                  });
                }
              }}
              onAddOption={(questionId) => {
                const questions = getQuestions().map(q => 
                  q.id === questionId 
                    ? { ...q, options: [...(q.options || []), { label: '', value: '' }] }
                    : q
                );
                updateQuestions(questions);
              }}
              onUpdateOption={(questionId, optionIndex, value) => {
                const questions = getQuestions().map(q => {
                  if (q.id === questionId && q.options) {
                    const updatedOptions = [...q.options];
                    updatedOptions[optionIndex] = { label: value, value };
                    return { ...q, options: updatedOptions };
                  }
                  return q;
                });
                updateQuestions(questions);
              }}
              onDeleteOption={(questionId, optionIndex) => {
                const questions = getQuestions().map(q => {
                  if (q.id === questionId && q.options) {
                    const updatedOptions = q.options.filter((_, index) => index !== optionIndex);
                    return { ...q, options: updatedOptions };
                  }
                  return q;
                });
                updateQuestions(questions);
              }}
              reportUnsavedChanges={(isDirty) => setDirty(TAB_FLOW, isDirty)}
              chatbotId={chatbot.id}
              chatMode={(() => {
                const settings = getSettings();
                console.log('Current settings from context:', settings);
                console.log('ChatMode from settings:', settings.chatMode);
                return settings.chatMode || 'question_flow';
              })()}
              onChatModeChange={async (newChatMode) => {
                console.log('🔄 Chat mode change in PortalBuilderContent:', newChatMode);
                console.log('🔄 Current settings before update:', getSettings());
                
                // Update settings with the new chat mode
                updateSettings({ chatMode: newChatMode });
                console.log('🔄 Settings updated, new chatMode:', newChatMode);

                // Force immediate save for chat mode changes using the correct save function
                try {
                  console.log('🔄 Calling debouncedSave for chatMode...');
                  debouncedSave({ settings: { ...getSettings(), chatMode: newChatMode } });
                  console.log('✅ Chat mode save initiated via PortalBuilderContent');
                } catch (error) {
                  console.error('❌ Error saving chat mode via PortalBuilderContent:', error);
                }
              }}
            />
          </TabsContent>
          
          <TabsContent value={TAB_STYLE} className="space-y-4">
            <BuilderStyle
              theme={getTheme()}
              onUpdateTheme={(updatedTheme) => {
                updateTheme(updatedTheme);
                if (getTheme()) {
                  saveTheme({ ...getTheme()!, ...updatedTheme });
                }
              }}
              reportUnsavedChanges={(isDirty) => setDirty(TAB_STYLE, isDirty)}
            />
          </TabsContent>
          
          <TabsContent value={TAB_TRIGGERS} className="space-y-4">
            <BuilderTriggers
              chatbotId={chatbot.id}
              reportUnsavedChanges={(isDirty) => setDirty(TAB_TRIGGERS, isDirty)}
            />
          </TabsContent>
          
          <TabsContent value={TAB_KNOWLEDGE} className="space-y-4">
            <BuilderKnowledgeBase
              chatbotId={chatbot.id}
              reportUnsavedChanges={(isDirty) => setDirty(TAB_KNOWLEDGE, isDirty)}
            />
          </TabsContent>
          
          <TabsContent value={TAB_INTEGRATIONS} className="space-y-4">
            <BuilderIntegrations
              chatbotId={chatbot.id}
              reportUnsavedChanges={(isDirty) => setDirty(TAB_INTEGRATIONS, isDirty)}
            />
          </TabsContent>
          
          <TabsContent value={TAB_SETTINGS} className="space-y-4">
            <BuilderSettings
              chatbot={chatbot}
              onUpdateSettings={(updatedSettings) => {
                updateSettings(updatedSettings);
              }}
              reportUnsavedChanges={(isDirty) => setDirty(TAB_SETTINGS, isDirty)}
            />
          </TabsContent>
        </Tabs>
        
        <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes. Are you sure you want to leave this tab?
                Your changes will be lost if you don't save them.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={cancelTabSwitch}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={discardChangesAndSwitchTab}>Discard Changes</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </PortalLayout>
  );
};
